import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { message, Modal } from "antd";
import {
  Button,
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
} from "@mui/material";
import Spinner from "../../../components/common/Spinner";
import ApiCallService from "../../../Service/ApiCallService";
import Calendar from "react-calendar";
import SearchSelect from "react-select";
import "react-calendar/dist/Calendar.css";
import "./CreateCalendar.css";

const isOtherCalendarTypeOption = (type) =>
  type?.eventType?.trim().toUpperCase() === "OTHER" ||
  ["CASE COUNSELLING", "CASE COUNCELLING"].includes(
    type?.name?.trim().toUpperCase()
  );

const isOtherCalendarType = (calendarTypes, calendarTypeId) =>
  isOtherCalendarTypeOption(
    calendarTypes.find((type) => type._id === calendarTypeId)
  );

const CreateCalendar = () => {
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const loggedInUserId = localStorage.getItem("userId");
  const fileInputRef = useRef(null);

  const isEditMode = Boolean(eventId);

  const [loading, setLoading] = useState(false);
  const [calendarTypes, setCalendarTypes] = useState([]);
  const [calendarSubTypes, setCalendarSubTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [formData, setFormData] = useState({
    heading: "",
    subheading: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    hyperlink: "",
    calendarBookingType: "PMS",
    calendarTypeId: "",
    calendarSubTypeId: "",
    organisationType: "HO",
    stateId: "",
    chapterId: "",
    documents: [],
    userId: loggedInUserId,
    meetingType: "Online",
    meetingLocation: "",
    meetingAddress: "",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behaviour: "smooth" });
  }, []);

  useEffect(() => {
    fetchCalendarTypes();
    fetchStates();
    fetchChapters();
    fetchMonthEvents(new Date());
  }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchEventById();
    }
  }, [isEditMode, eventId]);

  useEffect(() => {
    if (
      formData.calendarTypeId &&
      !isOtherCalendarType(calendarTypes, formData.calendarTypeId)
    ) {
      fetchCalendarSubTypes(formData.calendarTypeId);
    } else {
      setCalendarSubTypes([]);
    }
  }, [formData.calendarTypeId, calendarTypes]);

  const fetchCalendarTypes = async () => {
    try {
      setLoading(true);

      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/calendar-types`
      );

      setCalendarTypes(res.data || []);
    } catch (error) {
      console.error("Error fetching calendar types:", error);
      message.error("Failed to fetch calendar types");
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarSubTypes = async (calendarTypeId) => {
    try {
      setLoading(true);

      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/Sub-Type/${calendarTypeId}`
      );
      setCalendarSubTypes(res.data || []);
    } catch (error) {
      console.error("Error fetching calendar subtypes:", error);
      message.error("Failed to fetch calendar subtypes");
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthEvents = async (date) => {
    try {
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      const formattedStart = monthStart.toISOString().split("T")[0];
      const formattedEnd = monthEnd.toISOString().split("T")[0];

      setLoading(true);

      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/calendarbooking/events?startDate=${formattedStart}&endDate=${formattedEnd}&loggedInUserId=${loggedInUserId}`
      );

      setEvents(res.data || []);
    } catch (error) {
      console.error("Error fetching events:", error);
      message.error("Error fetching events");
    } finally {
      setLoading(false);
    }
  };

  const fetchEventById = async () => {
    try {
      setLoading(true);

      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/calendarbooking/${eventId}`
      );

      const data = res.data;

      const calendarTypeId = data.calendarTypeId?._id || data.calendarTypeId || "";
      const calendarSubTypeId =
        data.calendarSubTypeId?._id || data.calendarSubTypeId || "";

      setFormData({
        heading: data.heading || "",
        subheading: data.subheading || "",
        startDate: getDateInputValue(data.startDate),
        startTime: getTimeInputValue(data.startDate),
        endDate: getDateInputValue(data.endDate),
        endTime: getTimeInputValue(data.endDate),
        hyperlink: data.hyperlink || "",
        calendarBookingType: data.calendarBookingType || "PMS",
        calendarTypeId,
        calendarSubTypeId,
        organisationType: data.organisationType || "HO",
        stateId: data.stateId?._id || data.stateId || "",
        chapterId: data.chapterId?._id || data.chapterId || "",
        documents: data.documents || [],
        userId: data.createdBy?._id || data.createdBy || loggedInUserId,
        meetingType: data.meetingType || "Online",
        meetingLocation: data.meetingLocation || "",
        meetingAddress: data.meetingAddress || "",
      });

    } catch (error) {
      console.error("Error fetching event data:", error);
      message.error("Failed to fetch event data");
    } finally {
      setLoading(false);
    }
  };

  const getResponseList = (response) =>
    Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];

  const fetchStates = async () => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/states`
      );
      setStates(getResponseList(res));
    } catch (error) {
      console.error("Error fetching states:", error);
      message.error("Failed to fetch states");
    }
  };

  const fetchChapters = async () => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/getAllChapters`
      );
      setChapters(getResponseList(res));
    } catch (error) {
      console.error("Error fetching chapters:", error);
      message.error("Failed to fetch chapters");
    }
  };

  const getDateInputValue = (dateValue) => {
    if (!dateValue) return "";

    if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }

    const date = new Date(dateValue);
    if (isNaN(date)) return "";

    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());

    return date.toISOString().slice(0, 10);
  };

  const getTimeInputValue = (dateValue) => {
    if (!dateValue || (typeof dateValue === "string" && !dateValue.includes("T"))) {
      return "";
    }

    if (
      typeof dateValue === "string" &&
      /T00:00:00(?:\.000)?Z$/.test(dateValue)
    ) {
      return "";
    }

    const date = new Date(dateValue);
    if (isNaN(date)) return "";

    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());

    return date.toISOString().slice(11, 16);
  };

  const combineDateAndTime = (dateValue, timeValue) => {
    if (!dateValue) return "";
    if (!timeValue) return dateValue;

    return new Date(`${dateValue}T${timeValue}`).toISOString();
  };

  const validateForm = useCallback((data) => {
    const newErrors = {};

    if (!data.heading?.trim()) newErrors.heading = "Heading is required.";
    if (!data.startDate) newErrors.startDate = "Start date required.";
    if (!data.endDate) newErrors.endDate = "End date required.";
    if (!data.calendarTypeId) newErrors.calendarTypeId = "Event type required.";
    if (!data.organisationType) {
      newErrors.organisationType = "Organisation type required.";
    }
    if (data.organisationType === "STATE" && !data.stateId) {
      newErrors.stateId = "State is required.";
    }
    if (data.organisationType === "CHAPTER" && !data.chapterId) {
      newErrors.chapterId = "Chapter is required.";
    }
    if (
      !isOtherCalendarType(calendarTypes, data.calendarTypeId) &&
      !data.calendarSubTypeId
    )
      newErrors.calendarSubTypeId = "Sub type required.";

    if (data.startDate && data.endDate) {
      if (data.endDate < data.startDate) {
        newErrors.endDate = "End date cannot be before start date.";
      }

      if (
        data.endDate === data.startDate &&
        data.startTime &&
        data.endTime &&
        data.endTime < data.startTime
      ) {
        newErrors.endTime = "End time cannot be before start time.";
      }
    }

    return newErrors;
  }, [calendarTypes]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedFormData = { ...formData };

    if (name === "calendarTypeId") {
      updatedFormData.calendarTypeId = value;
      updatedFormData.calendarSubTypeId = "";
    } else if (name === "organisationType") {
      updatedFormData.organisationType = value;
      updatedFormData.stateId = "";
      updatedFormData.chapterId = "";
    } else if (name === "stateId") {
      updatedFormData.stateId = value;
      updatedFormData.chapterId = "";
    } else if (
      name === "startDate" ||
      name === "endDate" ||
      name === "startTime" ||
      name === "endTime"
    ) {
      updatedFormData[name] = value;
    } else {
      updatedFormData[name] = value;
    }

    setFormData(updatedFormData);
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validateForm(updatedFormData));
  };

  const handleClearTime = (fieldName) => {
    const updatedFormData = {
      ...formData,
      [fieldName]: "",
    };

    setFormData(updatedFormData);
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    setErrors(validateForm(updatedFormData));
  };

  const handleMeetingTypeChange = (e) => {
    const value = e.target.value;

    const updatedFormData = {
      ...formData,
      meetingType: value,
      meetingLocation: value === "Online" ? "" : formData.meetingLocation,
      meetingAddress: value === "Online" ? "" : formData.meetingAddress,
      hyperlink: value === "Offline" ? "" : formData.hyperlink,
    };

    setFormData(updatedFormData);
    setTouched((prev) => ({ ...prev, meetingType: true }));
    setErrors(validateForm(updatedFormData));
  };

  const updateChapterSelection = useCallback((chapterId) => {
    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        chapterId: chapterId || "",
      };

      setErrors(validateForm(updatedFormData));
      return updatedFormData;
    });
    setTouched((prev) => ({ ...prev, chapterId: true }));
  }, [validateForm]);

  const handleFileUpload = (e) => {
    const filesList = e.target.files;
    if (!filesList) return;

    const files = Array.from(filesList);

    const fileDataArray = files.map((file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      url: URL.createObjectURL(file),
      file,
    }));

    setFormData((prev) => {
      const existingNames = new Set((prev.documents || []).map((f) => f.name));

      const newFiles = fileDataArray.filter((f) => !existingNames.has(f.name));

      return {
        ...prev,
        documents: [...(prev.documents || []), ...newFiles],
      };
    });
  };

  const handleDeleteFile = (index) => {
    const file = formData.documents[index];

    if (file?.url) {
      URL.revokeObjectURL(file.url);
    }

    setFormData((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index),
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    setTouched({
      heading: true,
      subheading: true,
      startDate: true,
      startTime: true,
      endDate: true,
      endTime: true,
      calendarTypeId: true,
      calendarSubTypeId: true,
      organisationType: true,
      stateId: true,
      chapterId: true,
      hyperlink: true,
      meetingAddress: true,
      meetingLocation: true,
      meetingType: true,
    });

    if (Object.keys(validationErrors).length) {
      message.error("Please fix the errors before submitting.");
      return;
    }

    try {
      setLoading(true);

      const updatedDocuments = [];

      for (let i = 0; i < formData.documents.length; i++) {
        if (typeof formData.documents[i] === "string") {
          updatedDocuments.push(formData.documents[i]);
          continue;
        }

        const file = formData.documents[i];
        const fileData = new FormData();
        fileData.append("file", file.file);

        const response = await ApiCallService.post(
          `${process.env.REACT_APP_BASE_URL}/upload/program/document`,
          fileData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        updatedDocuments.push(response.url || response.document);
      }

      const finalPostData = {
        ...formData,
        calendarSubTypeId: isOtherCalendarType(
          calendarTypes,
          formData.calendarTypeId
        )
          ? null
          : formData.calendarSubTypeId,
        startDate: combineDateAndTime(formData.startDate, formData.startTime),
        endDate: combineDateAndTime(formData.endDate, formData.endTime),
        documents: updatedDocuments,
        userId: loggedInUserId || formData.userId,
      };

      if (finalPostData.organisationType === "HO") {
        finalPostData.stateId = null;
        finalPostData.chapterId = null;
      }

      if (finalPostData.organisationType === "STATE") {
        finalPostData.chapterId = null;
      }

      delete finalPostData.startTime;
      delete finalPostData.endTime;

      if (finalPostData.meetingType === "Offline") {
        finalPostData.hyperlink = "";
      }

      if (finalPostData.meetingType === "Online") {
        finalPostData.meetingAddress = "";
        finalPostData.meetingLocation = "";
      }

      if (isEditMode) {
        await ApiCallService.put(
          `${process.env.REACT_APP_BASE_URL}/calendarbooking/${eventId}/update`,
          finalPostData
        );

        message.success("Event updated successfully!");
      } else {
        await ApiCallService.post(
          `${process.env.REACT_APP_BASE_URL}/calendarbooking/create`,
          finalPostData
        );

        message.success("Event created successfully!");
      }

      navigate("/events");
    } catch (error) {
      console.error("Error submitting event:", error);
      message.error("Error occurred during event submission");
    } finally {
      setLoading(false);
    }
  };

  const getFileExtension = (doc) => {
    const value = doc?.name || doc || "";
    return value.split(".").pop()?.toLowerCase();
  };

  const shouldShowCalendarSubType = !isOtherCalendarType(
    calendarTypes,
    formData.calendarTypeId
  );
  const chapterOptions = (chapters || []).map((chapter) => ({
    value: chapter._id,
    label: chapter.chapterName || chapter.name || "Unnamed Chapter",
  }));
  const selectedChapterOption =
    chapterOptions.find((option) => option.value === formData.chapterId) || null;

  return (
    <Paper
      variant="pageBg"
      elevation={0}
      sx={{
        display: "flex",
        gap: 4,
        alignItems: "flex-start",
        justifyContent: "center",
        flexWrap: "wrap",
      }}
    >
      <Spinner loading={loading} />

      <div className="calendar-container">
        <Calendar
          onActiveStartDateChange={({ activeStartDate }) => {
            setActiveStartDate(activeStartDate);
            fetchMonthEvents(activeStartDate);
          }}
          onClickDay={(date) => {
            const foundEvents = events.filter(
              (event) =>
                new Date(event.startDate).toDateString() ===
                date.toDateString()
            );

            if (foundEvents.length > 0) {
              setSelectedEvent(foundEvents);
              navigate(`/event-details/${foundEvents[0]._id}`);
            } else {
              Modal.info({
                title: "No Events",
                content: `No event found on ${date.toDateString()}`,
              });
            }
          }}
          tileContent={({ date }) => {
            const eventOnDate = events.filter(
              (event) =>
                new Date(event.startDate).toDateString() === date.toDateString()
            );

            if (eventOnDate.length > 0) {
              return (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "3px",
                    marginTop: "3px",
                  }}
                >
                  {eventOnDate.map((event, idx) => (
                    <div
                      key={idx}
                      style={{
                        width: "7px",
                        height: "7px",
                        borderRadius: "50%",
                        backgroundColor:
                          event?.calendarTypeId?.color || "#6c27b3",
                      }}
                    />
                  ))}
                </div>
              );
            }

            return null;
          }}
        />
      </div>

      <div className="calendar-right">
        <h2 className="calendar-heading">
          {isEditMode ? "Update Calendar Event" : "Create Calendar Event"}
        </h2>

        <form onSubmit={handleSubmit} className="calendar-form">
          <div className="calendar-row">
            <div className="calendar-field">
              <label>
                Heading <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="heading"
                value={formData.heading}
                onChange={handleChange}
                placeholder="Heading"
              />
              {touched.heading && errors.heading && (
                <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                  {errors.heading}
                </div>
              )}
            </div>

            <div className="calendar-field">
              <label>Sub Heading</label>
              <input
                type="text"
                name="subheading"
                value={formData.subheading}
                onChange={handleChange}
                placeholder="Sub-Heading"
              />
              {touched.subheading && errors.subheading && (
                <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                  {errors.subheading}
                </div>
              )}
            </div>
          </div>

          <div className="calendar-row">
            <div className="calendar-field">
              <label>
                Start Date <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
              />
              {touched.startDate && errors.startDate && (
                <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                  {errors.startDate}
                </div>
              )}
            </div>

            <div className="calendar-field">
              <label>Start Time</label>
              <div className="calendar-time-control">
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="calendar-clear-time-btn"
                  onClick={() => handleClearTime("startTime")}
                  disabled={!formData.startTime}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          <div className="calendar-row">
            <div className="calendar-field">
              <label>
                End Date <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
              />
              {touched.endDate && errors.endDate && (
                <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                  {errors.endDate}
                </div>
              )}
            </div>

            <div className="calendar-field">
              <label>End Time</label>
              <div className="calendar-time-control">
                <input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="calendar-clear-time-btn"
                  onClick={() => handleClearTime("endTime")}
                  disabled={!formData.endTime}
                >
                  Clear
                </button>
              </div>
              {touched.endTime && errors.endTime && (
                <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                  {errors.endTime}
                </div>
              )}
            </div>
          </div>

          <div className="calendar-row">
            <div className="calendar-field">
              <label>
                Mode of Meeting <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                name="meetingType"
                value={formData.meetingType}
                onChange={handleMeetingTypeChange}
                size="small"
              >
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Offline">Offline</MenuItem>
              </Select>
            </div>

            {formData.meetingType !== "Offline" && (
              <div className="calendar-field">
                <label>Meeting Link</label>
                <input
                  type="url"
                  name="hyperlink"
                  value={formData.hyperlink}
                  onChange={handleChange}
                  placeholder="Enter meeting link"
                />
                {touched.hyperlink && errors.hyperlink && (
                  <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                    {errors.hyperlink}
                  </div>
                )}
              </div>
            )}

            {formData.meetingType === "Offline" && (
              <div className="calendar-field">
                <label>Google Map Link</label>
                <input
                  type="text"
                  name="meetingLocation"
                  value={formData.meetingLocation}
                  onChange={handleChange}
                  placeholder="Enter map link"
                />
              </div>
            )}
          </div>

          {formData.meetingType === "Offline" && (
            <div className="calendar-row">
              <div className="calendar-field">
                <label>Meeting Address</label>
                <input
                  type="text"
                  name="meetingAddress"
                  value={formData.meetingAddress}
                  onChange={handleChange}
                  placeholder="Enter meeting address"
                />
                {touched.meetingAddress && errors.meetingAddress && (
                  <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                    {errors.meetingAddress}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="calendar-row">
            <div className="calendar-field">
              <label>
                Event Type <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                name="calendarTypeId"
                value={formData.calendarTypeId}
                onChange={handleChange}
                displayEmpty
                size="small"
              >
                <MenuItem value="">Select Calendar Type</MenuItem>

                {[...calendarTypes]
                  .sort(
                    (firstType, secondType) =>
                      Number(isOtherCalendarTypeOption(firstType)) -
                      Number(isOtherCalendarTypeOption(secondType))
                  )
                  .map((type) => (
                    <MenuItem key={type._id} value={type._id}>
                      <div
                        style={{ display: "flex", alignItems: "center", gap: 8 }}
                      >
                        {!isOtherCalendarTypeOption(type) && (
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: type.color,
                            }}
                          />
                        )}
                        {type.name}
                      </div>
                    </MenuItem>
                  ))}
              </Select>
              {touched.calendarTypeId && errors.calendarTypeId && (
                <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                  {errors.calendarTypeId}
                </div>
              )}
            </div>

            {shouldShowCalendarSubType && (
              <div className="calendar-field">
                <label>
                  Event Sub Type <span style={{ color: "red" }}>*</span>
                </label>
                <Select
                  name="calendarSubTypeId"
                  value={formData.calendarSubTypeId}
                  onChange={handleChange}
                  displayEmpty
                  size="small"
                  disabled={!formData.calendarTypeId}
                >
                  <MenuItem value="">Select Calendar Sub Type</MenuItem>

                  {calendarSubTypes.map((subType) => (
                    <MenuItem key={subType._id} value={subType._id}>
                      {subType?.programId?.name ||
                        subType?.name ||
                        "Unnamed Sub Type"}
                    </MenuItem>
                  ))}
                </Select>

                {touched.calendarSubTypeId && errors.calendarSubTypeId && (
                  <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                    {errors.calendarSubTypeId}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="calendar-row">
            <div className="calendar-field">
              <label>
                Organiser <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                name="organisationType"
                value={formData.organisationType}
                onChange={handleChange}
                size="small"
              >
                <MenuItem value="HO">HO</MenuItem>
                <MenuItem value="STATE">State</MenuItem>
                <MenuItem value="CHAPTER">Chapter</MenuItem>
              </Select>
              {touched.organisationType && errors.organisationType && (
                <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                  {errors.organisationType}
                </div>
              )}
            </div>

            {formData.organisationType === "STATE" && (
              <div className="calendar-field">
                <label>
                  State <span style={{ color: "red" }}>*</span>
                </label>
                <Select
                  name="stateId"
                  value={formData.stateId}
                  onChange={handleChange}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="">Select State</MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state._id} value={state._id}>
                      {state.StateName || state.stateName || state.name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.stateId && errors.stateId && (
                  <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                    {errors.stateId}
                  </div>
                )}
              </div>
            )}

            {formData.organisationType === "CHAPTER" && (
              <div className="calendar-field">
                <label>
                  Chapter <span style={{ color: "red" }}>*</span>
                </label>
                <SearchSelect
                  options={chapterOptions}
                  value={selectedChapterOption}
                  onChange={(selectedOption) =>
                    updateChapterSelection(selectedOption?.value || "")
                  }
                  isSearchable
                  isClearable
                  placeholder="Search chapter by name"
                  classNamePrefix="calendar-react-select"
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      minHeight: 40,
                      borderRadius: 4,
                    }),
                  }}
                />
                {touched.chapterId && errors.chapterId && (
                  <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                    {errors.chapterId}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="calendar-row">
            <div className="calendar-field">
              <label>Upload Document</label>
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                ref={fileInputRef}
              />
            </div>
          </div>

          {Array.isArray(formData.documents) && formData.documents.length > 0 && (
            <div className="calendar-document-preview preview-container">
              {formData.documents.map((doc, index) => {
                const url = doc?.url || doc;
                const ext = getFileExtension(doc);

                const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(
                  ext
                );
                const isVideo = ["mp4", "mov", "webm", "mkv"].includes(ext);
                const isAudio = ["mp3", "wav", "aac"].includes(ext);
                const isPDF = ext === "pdf";

                return (
                  <div key={index} className="preview-item">
                    <button
                      type="button"
                      style={{ color: "white" }}
                      className="preview-delete-btn"
                      onClick={() => handleDeleteFile(index)}
                    >
                      ✕
                    </button>

                    {isImage && (
                      <img src={url} className="preview-media" alt="preview" />
                    )}

                    {isVideo && (
                      <video src={url} controls className="preview-media" />
                    )}

                    {isAudio && (
                      <audio controls className="preview-audio">
                        <source src={url} />
                      </audio>
                    )}

                    {isPDF && (
                      <iframe
                        src={url}
                        className="preview-media"
                        title="PDF Preview"
                      />
                    )}

                    {!isImage && !isVideo && !isAudio && !isPDF && (
                      <iframe src={url} className="preview-media" title="Preview" />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button type="submit" className="calendar-submit-btn">
            {isEditMode ? "Update" : "Submit"}
          </button>
        </form>
      </div>
    </Paper>
  );
};

export default CreateCalendar;
